import type { LiveSummaryTag } from "@storytell/ui/live-summary";
import { type Component, For, type JSX, Show } from "solid-js";
import { useScene, useTyping } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import {
  PeopleStack,
  SessionPill,
  SessionWindow,
  startedLabel,
  Swap,
  THEM_COLOR,
  type TranscriptEntry,
  YOU_COLOR,
} from "./internal/chief";
import { NotesLine, NotesPage, NotesTopic } from "./internal/notes";
import { at, Cursor, MockScene, type Person } from "./internal/parts";
import { ScaledFrame } from "./ScaledFrame";

/** A transcript line; `unnamed` lines carry the voice nobody has named yet. */
type SpeakersLine = TranscriptEntry & { unnamed?: boolean };

export type SpeakersData = {
  project: string;
  session: string;
  started: string;
  clock: string;
  people: Person[];
  /** The name the unnamed voice is given. */
  name: string;
  /** The organization's named voices, as the picker lists them before anything is typed. */
  known: string[];
  transcript: SpeakersLine[];
  /** The line the voice says after it is named. */
  next: TranscriptEntry;
  topic: string;
  notes: { text: string; tag?: LiveSummaryTag }[];
  /** The to-do from that line, owned by the named voice. */
  todo: { text: string; tag: LiveSummaryTag; owner: string };
};

// Seconds into the scene. The cursor glides for about 1.1s before it clicks.
const T = {
  pickCursor: 0.8,
  open: 1.9,
  typing: 2.9,
  addCursor: 3.2,
  name: 4.4,
  stagger: 0.15,
  next: 5.3,
  notes: 6.0,
  todo: 6.4,
};

// Stands in for the name while the Add row's sentence is split around it.
const NAME_MARK = "⁣";

/**
 * Naming a voice, played once: the transcript reads "Speaker 2", the label
 * opens the desktop app's speaker picker and the name is typed in, every line
 * that voice already said takes the name, the next one arrives with it, and
 * over on Notes the to-do it produces names its owner.
 */
export const SpeakersMockup: Component<{ data: SpeakersData; class?: string }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const scene = useScene();
  const typed = useTyping(() => props.data.name, scene, T.typing * 1000);
  const unnamed = () => props.data.transcript.flatMap((line, i) => (line.unnamed ? [i] : []));
  const pills = () =>
    [props.data.todo.tag, ...props.data.notes.flatMap((note) => (note.tag ? [note.tag] : []))]
      .filter((tag, i, all) => all.findIndex((other) => other.id === tag.id) === i)
      .map((tag) => ({
        tag,
        count: [props.data.todo, ...props.data.notes].filter((note) => note.tag?.id === tag.id).length,
      }));
  return (
    <MockScene scene={scene} label={t("marketing.mock.speakers.alt")} class={props.class}>
      <ScaledFrame chrome="app" width={760} height={560} narrow={{ width: 400, height: 760 }}>
        <SessionWindow
          project={props.data.project}
          title={props.data.session}
          facts={`${t("marketing.mock.live")} · ${startedLabel(props.data.started, currentLanguage())}`}
          live
          tab="transcript"
          next={[{ tab: "notes", at: T.notes }]}
          bar={<SessionPill clock={props.data.clock} people={<PeopleStack people={props.data.people} />} />}
        >
          <div class="grid h-full">
            <div class="exit col-start-1 row-start-1 px-5 py-4" style={at(T.notes)}>
              <For each={props.data.transcript}>
                {(line, i) => {
                  const order = () => unnamed().indexOf(i());
                  const picked = () => order() === unnamed().length - 1;
                  return (
                    <Line
                      line={line}
                      rename={
                        line.unnamed
                          ? { to: props.data.name, at: T.name + order() * T.stagger, picked: picked() }
                          : undefined
                      }
                    >
                      <Show when={line.unnamed && picked()}>
                        <Picker known={props.data.known} typed={typed()} />
                      </Show>
                    </Line>
                  );
                }}
              </For>
              <Line line={{ ...props.data.next, appearAt: T.next }} />
            </div>
            <div class="card-appear col-start-1 row-start-1" style={at(T.notes)}>
              <NotesPage
                project={props.data.project}
                title={props.data.session}
                meta={startedLabel(props.data.started, currentLanguage())}
                total={props.data.notes.length + 1}
                pills={pills()}
              >
                <NotesTopic text={props.data.topic} />
                <For each={props.data.notes}>{(note) => <NotesLine text={note.text} tag={note.tag} />}</For>
                <NotesLine
                  text={props.data.todo.text}
                  tag={props.data.todo.tag}
                  owner={props.data.todo.owner}
                  appearAt={T.todo}
                  tagAt={T.todo + 0.4}
                />
              </NotesPage>
            </div>
          </div>
        </SessionWindow>
      </ScaledFrame>
    </MockScene>
  );
};

/**
 * One transcript block. A `rename` swaps the whole header row, so the new name
 * sits against its timestamp rather than in the old label's width, and lights
 * the name for a moment as it lands. `picked` is the label the cursor clicks to
 * open the picker.
 */
const Line: Component<{
  line: SpeakersLine;
  rename?: { to: string; at: number; picked: boolean };
  children?: JSX.Element;
}> = (props) => {
  const header = (label: JSX.Element) => (
    <span class="mb-1 flex items-baseline gap-2">
      <span class="relative min-w-0 text-xs font-semibold" style={{ color: props.line.local ? YOU_COLOR : THEM_COLOR }}>
        {label}
      </span>
      <span class="shrink-0 text-xs text-muted-text tabular-nums">{props.line.time}</span>
    </span>
  );
  return (
    <div
      class={`relative mb-3 ${props.line.appearAt === undefined ? "" : "msg-in"}`}
      style={at(props.line.appearAt ?? 0)}
    >
      <Show when={props.rename} fallback={header(props.line.speaker)}>
        {(rename) => (
          <Swap
            swapAt={rename().at}
            before={header(
              <span
                class="relative inline-block"
                classList={{ press: rename().picked }}
                style={rename().picked ? at(T.open - 0.05) : {}}
              >
                {props.line.speaker}
                <Show when={rename().picked}>
                  <Cursor start={T.pickCursor} />
                </Show>
              </span>
            )}
            after={header(
              <>
                <span
                  class="blip absolute -inset-x-1.5 -inset-y-0.5 rounded bg-speaker-them/20"
                  style={at(rename().at)}
                />
                <span class="relative">{rename().to}</span>
              </>
            )}
          />
        )}
      </Show>
      <p class="max-w-[72ch] text-sm leading-relaxed text-primary-text">{props.line.text}</p>
      {props.children}
    </div>
  );
};

/**
 * The speaker picker as the desktop app draws it under a label: the search
 * field, the organization's named voices filtered by what is typed, and the
 * row that adds a new person. Every row is mounted from the start, because a
 * staged element's delay counts from when it is mounted.
 */
const Picker: Component<{ known: string[]; typed: string }> = (props) => {
  const { t } = useI18n();
  const matches = () => props.known.filter((name) => name.toLowerCase().includes(props.typed.toLowerCase()));
  const add = () => t("marketing.mock.speakers.add", { name: NAME_MARK }).split(NAME_MARK);
  return (
    <div class="card-appear absolute inset-s-0 top-5 z-20" style={at(T.open)}>
      <div class="exit w-60 rounded-xl border border-primary-border bg-tile shadow-xl" style={at(T.name)}>
        <div class="border-b border-primary-border p-2">
          <div class="flex h-6 items-center rounded-sm border border-(--mac-accent) bg-canvas px-2 text-xs">
            <Show
              when={props.typed}
              fallback={<span class="truncate text-muted-text">{t("marketing.mock.speakers.find")}</span>}
            >
              <span class="text-primary-text">{props.typed}</span>
            </Show>
            <span class="ms-px h-3.5 w-px bg-primary-text" />
          </div>
        </div>
        <div class="p-1">
          <For each={matches()}>{(name) => <p class="rounded-sm px-2 py-1.5 text-xs text-primary-text">{name}</p>}</For>
          <p
            class="press relative rounded-sm bg-panel-hover px-2 py-1.5 text-xs text-primary-text"
            classList={{ invisible: !props.typed }}
            style={at(T.name - 0.05)}
          >
            {add()[0]}
            <span class="font-semibold">{props.typed}</span>
            {add()[1]}
            <Cursor start={T.addCursor} />
          </p>
        </div>
      </div>
    </div>
  );
};
